import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_WIDTH = 800;
const SVG_HEIGHT = 450;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 30 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: { fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' },
  subtitle: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px' },
  viewToggle: {
    display: 'flex',
    gap: '4px',
    marginBottom: '16px',
  },
  viewButton: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#6b7280',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  viewButtonActive: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid #3b82f6',
    background: '#eff6ff',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  summaryRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  summaryCard: {
    flex: '1 1 140px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px',
    minWidth: '120px',
  },
  summaryLabel: {
    fontSize: '10px',
    fontWeight: 500,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '4px',
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1f2937',
  },
  container: {
    overflowX: 'auto',
    position: 'relative',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '260px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginTop: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    verticalAlign: 'middle',
  },
};

/**
 * Compute circular layout for regime nodes within the available area.
 */
function computeNodePositions(regimes, width, height) {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) * 0.3;
  const n = regimes.length;

  return regimes.map((regime, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    return {
      ...regime,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });
}

/**
 * Build a quadratic bezier curved path between two different nodes.
 */
function buildTransitionPath(source, target, cx, cy) {
  const midX = (source.x + target.x) / 2;
  const midY = (source.y + target.y) / 2;
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  const fromCenter = { x: midX - cx, y: midY - cy };
  const fromCenterDist = Math.sqrt(fromCenter.x * fromCenter.x + fromCenter.y * fromCenter.y) || 1;

  const curvature = dist * 0.25;
  const cpX = midX + (fromCenter.x / fromCenterDist) * curvature;
  const cpY = midY + (fromCenter.y / fromCenterDist) * curvature;

  return { path: `M ${source.x},${source.y} Q ${cpX},${cpY} ${target.x},${target.y}`, cpX, cpY };
}

/**
 * Build a self-loop path that extends outward from center.
 */
function buildSelfLoopPath(node, cx, cy, nodeRadius) {
  const awayX = node.x - cx;
  const awayY = node.y - cy;
  const awayDist = Math.sqrt(awayX * awayX + awayY * awayY) || 1;
  const normX = awayX / awayDist;
  const normY = awayY / awayDist;
  const perpX = -normY;
  const perpY = normX;

  const loopRadius = nodeRadius + 8;
  const spread = 0.5;

  const startX = node.x + nodeRadius * (normX * Math.cos(spread) + perpX * Math.sin(spread));
  const startY = node.y + nodeRadius * (normY * Math.cos(spread) + perpY * Math.sin(spread));
  const endX = node.x + nodeRadius * (normX * Math.cos(spread) - perpX * Math.sin(spread));
  const endY = node.y + nodeRadius * (normY * Math.cos(spread) - perpY * Math.sin(spread));

  const loopDist = loopRadius * 1.8;
  const cp1X = node.x + normX * loopDist + perpX * loopRadius * 0.6;
  const cp1Y = node.y + normY * loopDist + perpY * loopRadius * 0.6;
  const cp2X = node.x + normX * loopDist - perpX * loopRadius * 0.6;
  const cp2Y = node.y + normY * loopDist - perpY * loopRadius * 0.6;

  const labelX = node.x + normX * loopDist;
  const labelY = node.y + normY * loopDist;

  return {
    path: `M ${startX},${startY} C ${cp1X},${cp1Y} ${cp2X},${cp2Y} ${endX},${endY}`,
    labelX,
    labelY,
  };
}

export default function PriceRegimeTransitionDiagram({ data }) {
  const svgRef = useRef(null);
  const [activeView, setActiveView] = useState('diagram');
  const [tooltip, setTooltip] = useState(null);

  // Transition Diagram rendering
  useEffect(() => {
    if (activeView !== 'diagram') return;
    if (!data || !data.regimes || data.regimes.length === 0 || !data.transitionMatrix) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { regimes, transitionMatrix, currentRegime } = data;
    const innerW = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('width', '100%')
      .attr('viewBox', `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    // Defs
    const defs = svg.append('defs');

    defs.append('marker')
      .attr('id', 'prtd-arrow')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 8)
      .attr('refY', 5)
      .attr('markerWidth', 8)
      .attr('markerHeight', 8)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', 'M 0 1 L 10 5 L 0 9 Z')
      .attr('fill', '#6b7280');

    defs.append('marker')
      .attr('id', 'prtd-arrow-self')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 8)
      .attr('refY', 5)
      .attr('markerWidth', 7)
      .attr('markerHeight', 7)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', 'M 0 1 L 10 5 L 0 9 Z')
      .attr('fill', '#9ca3af');

    const glowFilter = defs.append('filter')
      .attr('id', 'prtd-glow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    glowFilter.append('feGaussianBlur').attr('stdDeviation', '4').attr('result', 'blur');
    glowFilter.append('feComposite').attr('in', 'SourceGraphic').attr('in2', 'blur').attr('operator', 'over');

    // Node positions
    const nodes = computeNodePositions(regimes, innerW, innerH);
    const cx = innerW / 2;
    const cy = innerH / 2;
    const nodeRadius = 38;

    const g = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Build flat transition list from matrix
    const transitions = [];
    for (let i = 0; i < regimes.length; i++) {
      for (let j = 0; j < regimes.length; j++) {
        const prob = transitionMatrix[i] && transitionMatrix[i][j];
        if (prob && prob > 0.01) {
          transitions.push({
            fromIdx: i,
            toIdx: j,
            from: regimes[i].id,
            to: regimes[j].id,
            probability: prob,
          });
        }
      }
    }

    // Stroke scale
    const probs = transitions.map((t) => t.probability);
    const probMin = Math.min(...probs);
    const probMax = Math.max(...probs);
    const strokeScale = (prob) => {
      if (probMin === probMax) return 3;
      const t = (prob - probMin) / (probMax - probMin);
      return 1.5 + t * 5;
    };

    // Draw transitions
    transitions.forEach((trans) => {
      const sourceNode = nodes[trans.fromIdx];
      const targetNode = nodes[trans.toIdx];
      if (!sourceNode || !targetNode) return;

      const strokeW = strokeScale(trans.probability);
      const isSelf = trans.from === trans.to;

      if (isSelf) {
        const loop = buildSelfLoopPath(sourceNode, cx, cy, nodeRadius);

        g.append('path')
          .attr('d', loop.path)
          .attr('fill', 'none')
          .attr('stroke', sourceNode.color)
          .attr('stroke-width', strokeW)
          .attr('stroke-opacity', 0.5)
          .attr('marker-end', 'url(#prtd-arrow-self)')
          .style('cursor', 'pointer')
          .on('mouseenter', function (event) {
            d3.select(this).attr('stroke-opacity', 0.9);
            setTooltip({
              x: event.clientX + 14, y: event.clientY - 14,
              content: `${sourceNode.label} self-loop: ${(trans.probability * 100).toFixed(1)}%`,
            });
          })
          .on('mousemove', function (event) {
            setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
          })
          .on('mouseleave', function () {
            d3.select(this).attr('stroke-opacity', 0.5);
            setTooltip(null);
          });

        // Self-loop label
        g.append('text')
          .attr('x', loop.labelX)
          .attr('y', loop.labelY)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', '#6b7280')
          .attr('pointer-events', 'none')
          .text(`${(trans.probability * 100).toFixed(0)}%`);
      } else {
        const { cpX, cpY } = buildTransitionPath(sourceNode, targetNode, cx, cy);

        // Shorten to start/end at node edges
        const dx1 = cpX - sourceNode.x;
        const dy1 = cpY - sourceNode.y;
        const dist1 = Math.sqrt(dx1 * dx1 + dy1 * dy1) || 1;
        const sx = sourceNode.x + (dx1 / dist1) * nodeRadius;
        const sy = sourceNode.y + (dy1 / dist1) * nodeRadius;

        const dx2 = cpX - targetNode.x;
        const dy2 = cpY - targetNode.y;
        const dist2 = Math.sqrt(dx2 * dx2 + dy2 * dy2) || 1;
        const tx = targetNode.x + (dx2 / dist2) * (nodeRadius + 6);
        const ty = targetNode.y + (dy2 / dist2) * (nodeRadius + 6);

        const shortenedPath = `M ${sx},${sy} Q ${cpX},${cpY} ${tx},${ty}`;

        g.append('path')
          .attr('d', shortenedPath)
          .attr('fill', 'none')
          .attr('stroke', '#6b7280')
          .attr('stroke-width', strokeW)
          .attr('stroke-opacity', 0.45)
          .attr('marker-end', 'url(#prtd-arrow)')
          .style('cursor', 'pointer')
          .on('mouseenter', function (event) {
            d3.select(this).attr('stroke-opacity', 0.9).attr('stroke', '#1f2937');
            setTooltip({
              x: event.clientX + 14, y: event.clientY - 14,
              content: `${sourceNode.label} \u2192 ${targetNode.label}: ${(trans.probability * 100).toFixed(1)}%`,
            });
          })
          .on('mousemove', function (event) {
            setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
          })
          .on('mouseleave', function () {
            d3.select(this).attr('stroke-opacity', 0.45).attr('stroke', '#6b7280');
            setTooltip(null);
          });

        // Edge label
        const labelX = 0.25 * sx + 0.5 * cpX + 0.25 * tx;
        const labelY = 0.25 * sy + 0.5 * cpY + 0.25 * ty;
        const labelText = `${(trans.probability * 100).toFixed(0)}%`;

        g.append('rect')
          .attr('x', labelX - 16)
          .attr('y', labelY - 8)
          .attr('width', 32)
          .attr('height', 16)
          .attr('rx', 3)
          .attr('fill', '#fff')
          .attr('fill-opacity', 0.85)
          .attr('pointer-events', 'none');

        g.append('text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', '#374151')
          .attr('pointer-events', 'none')
          .text(labelText);
      }
    });

    // Draw nodes
    const nodeGroups = g.selectAll('.prtd-node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'prtd-node')
      .attr('transform', (d) => `translate(${d.x}, ${d.y})`)
      .style('cursor', 'pointer');

    // Glow ring for current regime
    nodeGroups
      .filter((d) => d.id === currentRegime)
      .append('circle')
      .attr('r', nodeRadius + 6)
      .attr('fill', 'none')
      .attr('stroke', (d) => d.color)
      .attr('stroke-width', 3)
      .attr('stroke-opacity', 0.4)
      .attr('filter', 'url(#prtd-glow)');

    nodeGroups
      .filter((d) => d.id === currentRegime)
      .append('circle')
      .attr('r', nodeRadius + 4)
      .attr('fill', 'none')
      .attr('stroke', (d) => d.color)
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6);

    // Node circles
    nodeGroups.append('circle')
      .attr('r', nodeRadius)
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', 0.12)
      .attr('stroke', (d) => d.color)
      .attr('stroke-width', (d) => d.id === currentRegime ? 3 : 2);

    nodeGroups.append('circle')
      .attr('r', nodeRadius * 0.65)
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', 0.25);

    // Node label
    nodeGroups.append('text')
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 700)
      .attr('fill', '#1f2937')
      .attr('pointer-events', 'none')
      .text((d) => d.label);

    // Frequency sublabel
    nodeGroups.append('text')
      .attr('y', 8)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 500)
      .attr('fill', '#6b7280')
      .attr('pointer-events', 'none')
      .text((d) => `${(d.frequency * 100).toFixed(0)}% freq`);

    // Avg duration sublabel
    nodeGroups.append('text')
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 500)
      .attr('fill', '#9ca3af')
      .attr('pointer-events', 'none')
      .text((d) => `avg ${d.avgDuration.toFixed(1)} per`);

    // CURRENT badge
    nodeGroups
      .filter((d) => d.id === currentRegime)
      .append('text')
      .attr('y', -(nodeRadius + 14))
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 700)
      .attr('fill', (d) => d.color)
      .attr('pointer-events', 'none')
      .text('CURRENT');

    // Node hover
    nodeGroups
      .on('mouseenter', function (event, d) {
        d3.select(this).select('circle').transition().duration(150).attr('stroke-width', 4);
        const row = transitionMatrix[regimes.findIndex((r) => r.id === d.id)];
        const outText = row
          ? regimes.map((r, j) => `${r.label}: ${(row[j] * 100).toFixed(0)}%`).join(', ')
          : '';
        setTooltip({
          x: event.clientX + 14, y: event.clientY - 14,
          content: `${d.label}${d.id === currentRegime ? ' (CURRENT)' : ''}\nFrequency: ${(d.frequency * 100).toFixed(0)}% | Avg duration: ${d.avgDuration.toFixed(1)} periods\nTransitions out: ${outText}`,
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
      })
      .on('mouseleave', function () {
        d3.select(this).select('circle').transition().duration(150)
          .attr('stroke-width', (d) => d.id === currentRegime ? 3 : 2);
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, activeView]);

  // Timeline rendering
  useEffect(() => {
    if (activeView !== 'timeline') return;
    if (!data || !data.timeline || data.timeline.length === 0 || !data.regimes) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { regimes, timeline, currentRegime } = data;
    const innerW = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerH = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('width', '100%')
      .attr('viewBox', `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Build segments by grouping consecutive same-regime entries
    const segments = [];
    let currentSeg = null;
    timeline.forEach((entry, i) => {
      if (!currentSeg || currentSeg.regime !== entry.regime) {
        if (currentSeg) {
          currentSeg.endIdx = i - 1;
          segments.push(currentSeg);
        }
        currentSeg = { regime: entry.regime, startIdx: i, startDate: entry.date, endIdx: i, endDate: entry.date };
      } else {
        currentSeg.endIdx = i;
        currentSeg.endDate = entry.date;
      }
    });
    if (currentSeg) {
      segments.push(currentSeg);
    }

    const totalEntries = timeline.length;
    const barHeight = 40;
    const barY = innerH / 2 - barHeight / 2;

    // Time axis
    const parseDate = d3.timeParse('%Y-%m-%d');
    const dates = timeline.map((t) => parseDate(t.date)).filter(Boolean);
    let xScale;

    if (dates.length === timeline.length && dates[0]) {
      xScale = d3.scaleTime()
        .domain([dates[0], dates[dates.length - 1]])
        .range([0, innerW]);
    } else {
      xScale = d3.scaleLinear()
        .domain([0, totalEntries - 1])
        .range([0, innerW]);
    }

    // Draw segments
    segments.forEach((seg) => {
      const regime = regimes.find((r) => r.id === seg.regime);
      if (!regime) return;

      let x0, x1;
      if (dates.length === timeline.length && dates[0]) {
        x0 = xScale(parseDate(seg.startDate));
        x1 = seg.endIdx < totalEntries - 1
          ? xScale(parseDate(timeline[seg.endIdx + 1].date))
          : innerW;
      } else {
        x0 = xScale(seg.startIdx);
        x1 = seg.endIdx < totalEntries - 1 ? xScale(seg.endIdx + 1) : innerW;
      }

      const segWidth = Math.max(x1 - x0, 2);

      g.append('rect')
        .attr('x', x0)
        .attr('y', barY)
        .attr('width', segWidth)
        .attr('height', barHeight)
        .attr('fill', regime.color)
        .attr('fill-opacity', 0.7)
        .attr('rx', seg.startIdx === 0 ? 4 : 0)
        .attr('ry', seg.startIdx === 0 ? 4 : 0)
        .style('cursor', 'pointer')
        .on('mouseenter', function (event) {
          d3.select(this).attr('fill-opacity', 1);
          const duration = seg.endIdx - seg.startIdx + 1;
          setTooltip({
            x: event.clientX + 14, y: event.clientY - 14,
            content: `${regime.label}\n${seg.startDate} to ${seg.endDate}\nDuration: ${duration} period${duration > 1 ? 's' : ''}`,
          });
        })
        .on('mousemove', function (event) {
          setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
        })
        .on('mouseleave', function () {
          d3.select(this).attr('fill-opacity', 0.7);
          setTooltip(null);
        });

      // Segment label if wide enough
      if (segWidth > 50) {
        g.append('text')
          .attr('x', x0 + segWidth / 2)
          .attr('y', barY + barHeight / 2)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', '#fff')
          .attr('pointer-events', 'none')
          .text(regime.label);
      }
    });

    // Border around the entire bar
    g.append('rect')
      .attr('x', 0)
      .attr('y', barY)
      .attr('width', innerW)
      .attr('height', barHeight)
      .attr('fill', 'none')
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('rx', 4);

    // Date axis
    if (dates.length === timeline.length && dates[0]) {
      const xAxis = d3.axisBottom(xScale)
        .ticks(6)
        .tickFormat(d3.timeFormat('%b %Y'));

      g.append('g')
        .attr('transform', `translate(0, ${barY + barHeight + 8})`)
        .call(xAxis)
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280');

      g.selectAll('.domain').attr('stroke', '#d1d5db');
      g.selectAll('.tick line').attr('stroke', '#e5e7eb');
    } else {
      // Index-based labels
      g.append('text')
        .attr('x', 0)
        .attr('y', barY + barHeight + 18)
        .attr('text-anchor', 'start')
        .attr('font-size', '10px')
        .attr('fill', '#9ca3af')
        .text(timeline[0].date || 'Start');

      g.append('text')
        .attr('x', innerW)
        .attr('y', barY + barHeight + 18)
        .attr('text-anchor', 'end')
        .attr('font-size', '10px')
        .attr('fill', '#9ca3af')
        .text(timeline[timeline.length - 1].date || 'End');
    }

    // Title label
    g.append('text')
      .attr('x', 0)
      .attr('y', barY - 12)
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Regime Timeline');

    // Current regime indicator
    if (currentRegime && segments.length > 0) {
      const lastSeg = segments[segments.length - 1];
      const lastRegime = regimes.find((r) => r.id === lastSeg.regime);
      if (lastRegime) {
        g.append('text')
          .attr('x', innerW)
          .attr('y', barY - 12)
          .attr('text-anchor', 'end')
          .attr('font-size', '10px')
          .attr('font-weight', 600)
          .attr('fill', lastRegime.color)
          .text(`Current: ${lastRegime.label}`);
      }
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, activeView]);

  // Empty state
  if (!data || !data.regimes || data.regimes.length === 0 || !data.transitionMatrix) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Regime Transitions</div>
        <div style={styles.subtitle}>Regime classification and Markov transition probabilities</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No regime transition data available
        </div>
      </div>
    );
  }

  const { regimes, currentRegime, stationaryDistribution } = data;
  const currentRegimeObj = regimes.find((r) => r.id === currentRegime);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Regime Transitions</div>
      <div style={styles.subtitle}>Regime classification and Markov transition probabilities</div>

      {/* Summary cards */}
      <div style={styles.summaryRow}>
        {/* Current regime card */}
        <div style={{
          ...styles.summaryCard,
          borderLeft: currentRegimeObj ? `3px solid ${currentRegimeObj.color}` : undefined,
        }}>
          <div style={styles.summaryLabel}>Current Regime</div>
          <div style={{
            ...styles.summaryValue,
            color: currentRegimeObj ? currentRegimeObj.color : '#1f2937',
          }}>
            {currentRegimeObj ? currentRegimeObj.label : 'Unknown'}
          </div>
        </div>

        {/* Avg duration per regime */}
        {regimes.map((r) => (
          <div key={r.id} style={styles.summaryCard}>
            <div style={styles.summaryLabel}>{r.label} Avg Duration</div>
            <div style={styles.summaryValue}>{r.avgDuration.toFixed(1)} <span style={{ fontSize: '11px', fontWeight: 400, color: '#6b7280' }}>periods</span></div>
          </div>
        ))}

        {/* Stationary distribution card */}
        {stationaryDistribution && stationaryDistribution.length === regimes.length && (
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Stationary Distribution</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
              {regimes.map((r, i) => (
                <span key={r.id} style={{ fontSize: '11px', fontWeight: 600, color: r.color }}>
                  {r.label}: {(stationaryDistribution[i] * 100).toFixed(0)}%
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* View toggle */}
      <div style={styles.viewToggle}>
        <button
          style={activeView === 'diagram' ? styles.viewButtonActive : styles.viewButton}
          onClick={() => setActiveView('diagram')}
        >
          Transition Diagram
        </button>
        <button
          style={activeView === 'timeline' ? styles.viewButtonActive : styles.viewButton}
          onClick={() => setActiveView('timeline')}
        >
          Timeline
        </button>
      </div>

      {/* SVG */}
      <div style={styles.container}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          {tooltip.content.split('\n').map((line, i) => (
            <div key={i} style={{ color: i === 0 ? '#1f2937' : '#6b7280', fontWeight: i === 0 ? 600 : 400, marginBottom: i < tooltip.content.split('\n').length - 1 ? '2px' : 0 }}>
              {line}
            </div>
          ))}
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        {regimes.map((r) => (
          <div key={r.id} style={styles.legendItem}>
            <span style={{ ...styles.legendSwatch, background: r.color }} />
            <span>{r.label} ({(r.frequency * 100).toFixed(0)}%)</span>
          </div>
        ))}
        <div style={styles.legendItem}>
          <span style={{ fontSize: '10px', color: '#6b7280' }}>
            Arrow thickness = transition probability
          </span>
        </div>
      </div>
    </div>
  );
}
